import Button from 'common/components/button/Button';
import Label from 'common/components/label/Label';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import styles from './RemoveVerificationModal.module.scss';

type Props = {
    isVisible: boolean;
    onRemove: () => void;
    onCancelRemove: () => void;
    onRemoveAndFinishReplacement: () => void;
    onRemoveAndStartReplacement?: () => void;
    verificationImmobilized?: boolean;
    lastVerificationImmobilized?: boolean;
    hasReplacement?: boolean;
    isReplacementFromThisVerification?: boolean;
};

const RemoveVerificationModal: React.FC<Props> = ({
    isVisible,
    onRemove,
    onCancelRemove,
    onRemoveAndFinishReplacement,
    onRemoveAndStartReplacement,
    verificationImmobilized,
    lastVerificationImmobilized,
    hasReplacement,
    isReplacementFromThisVerification
}: Props) => {
    const { t } = useTranslation();

    // Determine impacts
    const immobilizedStateChanges = verificationImmobilized !== lastVerificationImmobilized;
    const willBecomeImmobilized = immobilizedStateChanges && lastVerificationImmobilized;
    const willStopBeingImmobilized = immobilizedStateChanges && !lastVerificationImmobilized;
    const hasReplacementToDiscard = hasReplacement && isReplacementFromThisVerification;

    // Determine if we should show the "start replacement" or "finish replacement" button
    const showStartReplacementButton = willBecomeImmobilized && !hasReplacement;
    const showFinishReplacementButton = willStopBeingImmobilized && hasReplacement;

    const impacts: string[] = [];
    if (willBecomeImmobilized) {
        impacts.push(t('replacement_vehicles.remove_impact_will_be_immobilized'));
    }
    if (willStopBeingImmobilized) {
        impacts.push(t('replacement_vehicles.remove_impact_will_stop_being_immobilized'));
    }
    if (hasReplacementToDiscard) {
        impacts.push(t('replacement_vehicles.remove_impact_replacement_discarded'));
    }

    const customStyles = {
        content: {
            top: '15%',
            left: '32%',
            right: '32%',
            bottom: 'auto',
            backgroundClip: 'padding-box',
            backgroundColor: '#FFFFFF',
            border: '1px solid transparent',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            outline: '0 none',
        },
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
        }
    };

    return (
        <Modal isOpen={isVisible} style={customStyles}>
            <div className={styles.modalContent}>
                <div className={styles.titleContainer}>
                    <Label className={styles.textTitle}>{t('common.question')}</Label>
                </div>
                <div className={styles.messageContainer}>
                    <Label className={styles.introQuestion}>{t('replacement_vehicles.remove_impacts_intro')}</Label>
                    {impacts.length > 0 && (
                        <ul className={styles.impactsList}>
                            {impacts.map((impact, index) => (
                                <li key={index}>{impact}</li>
                            ))}
                        </ul>
                    )}
                    <Label className={styles.confirmQuestion}>{t('replacement_vehicles.remove_confirm_question')}</Label>
                </div>
                <div className={styles.buttonsContainer}>
                    <Button text={t('common.cancel')} preset={'secondary'} size={'small'} onClick={onCancelRemove} />
                    <Button text={t('car_verification.remove')} size={'small'} onClick={onRemove} /> 
                </div>
            </div>
        </Modal>
    );
}

export default RemoveVerificationModal;
